import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  Grid,
  Button,
  Stack,
  ButtonProps,
  Typography,
  Box,
  TextField,
  TextFieldProps,
  InputAdornment,
  Select,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled, alpha, lighten, useTheme } from '@mui/material/styles';
import { hex2rgb } from '@simplytech/simply.utils.colors';
import Iconify from '@simplytech/simply.general.iconify';

const phoneFieldId = 'fitness-5-option-3';

// TODO: Move to form creation
// function getButtonText(languageCode: Intl) {
//   switch (languageCode) {
//     case Intl.DA:
//       return { previous: 'Forrige', next: 'Næste', submit: 'Send' };
//     case Intl.EN:
//       return { previous: 'Previous', next: 'Next', submit: 'Submit' };
//     case Intl.DE:
//       return { previous: 'Zurück', next: 'Weiter', submit: 'Senden' };
//     case Intl.FR:
//       return { previous: 'Précédent', next: 'Suivant', submit: 'Envoyer' };
//     case Intl.NO:
//       return { previous: 'Forrige', next: 'Næste', submit: 'Send' };
//     case Intl.FI:
//       return { previous: 'Edellinen', next: 'Seuraava', submit: 'Lähetä' };
//     case Intl.SE:
//       return { previous: 'Föregående', next: 'Nästa', submit: 'Skicka' };
//     case Intl.IT:
//       return { previous: 'Precedente', next: 'Successiva', submit: 'Invia' };
//     case Intl.ES:
//       return { previous: 'Anterior', next: 'Siguiente', submit: 'Enviar' };
//     case Intl.CH:
//       return { previous: 'Zurück', next: 'Weiter', submit: 'Senden' };
//     case Intl.US:
//       return { previous: 'Previous', next: 'Next', submit: 'Submit' };
//     case Intl.CY:
//       return { previous: 'Blaenorol', next: 'Nesaf', submit: 'Anfon' };
//     case Intl.PT:
//       return { previous: 'Anterior', next: 'Seguinte', submit: 'Enviar' };
//     default:
//       return { previous: 'Previous', next: 'Next', submit: 'Send' };
//   }
// }

function isValidForm(
  questionIds: string[],
  questions: Question[],
  answers: Record<string, any>
) {
  return questionIds.every((id) => {
    if (typeof answers[id] === 'object') {
      console.log({ answers: Object.values(answers[id]) });
      const optionIds =
        questions.find((q) => q.id === id)?.options?.map((o) => o.id) || [];
      return optionIds.every((oId) =>
        oId === phoneFieldId
          ? answers[id][oId]?.code && answers[id][oId]?.phone
          : answers[id][oId]
      );
    }
    return answers[id];
  });
}

export enum Intl {
  DA = 'da',
  EN = 'en',
  DE = 'de',
  FR = 'fr',
  NO = 'no',
  FI = 'fi',
  SE = 'se',
  IT = 'it',
  ES = 'es',
  CH = 'ch',
  US = 'us',
  CY = 'cy',
  PT = 'pt',
  ID = 'id',
  AU = 'au',
  BR = 'br',
}
export enum LanguageCode {
  DK = 'DK',
  EN = 'EN',
}
export enum QuestionType {
  RADIO = 'radio',
  TEXT = 'text',
  LONG_TEXT = 'long_text',
  NUMBER = 'number',
  CONTACT = 'contact',
}
export interface QuestionOption {
  id: string;
  content: string;
}
export interface ContactOption {
  id: string;
  content: string;
  label: string;
}

export interface Question {
  id: string;
  content: string;
  type: QuestionType;
  options?: QuestionOption[] | ContactOption[];
  placeholder?: string;
  required?: boolean;
}

export type FormProps = {
  defaultCountryCode: string;
  answers: Record<string, any>;
  updateAnswer: (id: string, value: string | number) => void;
  questions: Question[];
  customColor: string;
  initialActive: string;
  handleSubmit: VoidFunction;
  previousText: string;
  submitText: string;
  nextText: string;
  submitting: boolean;
};

export function Form({
  defaultCountryCode,
  answers,
  updateAnswer,
  questions = [],
  initialActive,
  customColor,
  handleSubmit,
  previousText,
  submitText,
  nextText,
  submitting = false,
}: FormProps) {
  // const { previous, next, submit } = getButtonText(languageCode);
  const [active, setActive] = useState(initialActive);

  const handleNext = () => {
    setActive((prev) => {
      const currentIndex = questions.findIndex((q) => q.id === prev);
      const nextIndex = currentIndex + 1;
      if (nextIndex < questions.length) {
        return questions[nextIndex].id;
      }
      return prev;
    });
  };
  const handlePrevious = () => {
    setActive((prev) => {
      const currentIndex = questions.findIndex((q) => q.id === prev);
      const nextIndex = currentIndex - 1;
      if (nextIndex >= 0) {
        return questions[nextIndex].id;
      }
      return prev;
    });
  };

  const handleSelectOption = (id: string, answer: string) => {
    updateAnswer(id, answer);
    setTimeout(() => {
      handleNext();
    }, 200);
  };
  const handleAnswer = (id: string, value: string) => {
    updateAnswer(id, value);
  };
  const handleContactAnswer = (
    id: string,
    optionId: string,
    value: string,
    type?: 'countryCode'
  ) => {
    if (optionId === phoneFieldId) {
      if (type === 'countryCode') {
        updateAnswer(id, {
          ...answers[id],
          [optionId]: { ...answers[id][optionId], code: value },
        });
      } else {
        updateAnswer(id, {
          ...answers[id],
          [optionId]: { ...answers[id][optionId], phone: value },
        });
      }
    } else {
      updateAnswer(id, { ...answers[id], [optionId]: value });
    }
  };
  const questionIds = questions.map((q) => q.id);
  const isAllAnswered = isValidForm(questionIds, questions, answers);
  // const defaultCountryCode = (countries.find(
  //   (c) => c.languageCode === languageCode
  // )?.phone || Intl.DA) as Intl;
  return (
    <Grid container spacing={3} maxWidth={350}>
      <Grid item xs={12} md={12} sx={{ pt: 0, position: 'relative' }}>
        <Stack width="50%">
          <Stepper
            steps={questionIds}
            active={active}
            setActive={setActive}
            customColor={customColor}
          />
        </Stack>
        {questions.map((q) => (
          // <Fade in={q.id === active} timeout={500}>
          <Box
            key={q.id}
            sx={{ display: q.id === active ? 'inherit' : 'none' }}
          >
            <Typography fontSize={24} m={0} mt={1} mb={3} fontFamily="inherit">
              {q.content}
            </Typography>
            {q.type === QuestionType.NUMBER && (
              <NumberText
                placeholder={q.placeholder || q.content}
                customColor={customColor}
                handleAnswer={(value) => handleAnswer(q.id, value)}
              />
            )}
            {q.type === QuestionType.LONG_TEXT && (
              <LongText
                placeholder={q.placeholder || q.content}
                customColor={customColor}
                handleAnswer={(value) => handleAnswer(q.id, value)}
              />
            )}
            {q.type === QuestionType.RADIO && (
              <MultipleChoice
                value={answers[q.id]}
                handleSelectOption={(answer) =>
                  handleSelectOption(q.id, answer)
                }
                options={q.options as QuestionOption[]}
                customColor={customColor}
              />
            )}
            {q.type === QuestionType.CONTACT && (
              <ContactForm
                defaultCountryCode={defaultCountryCode}
                handleAnswer={(optionId, value, type) =>
                  handleContactAnswer(q.id, optionId, value, type)
                }
                options={q.options as ContactOption[]}
                customColor={customColor}
              />
            )}
          </Box>
          // {/* </Fade> */}
        ))}
      </Grid>
      <Grid item xs={12} md={12} sx={{ pt: 0 }}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Stack flexGrow={1}>
            {questions[0].id !== active && (
              <BrandButton
                size="large"
                customColor={customColor}
                onClick={handlePrevious}
              >
                {previousText}
              </BrandButton>
            )}
          </Stack>
          <Stack flexGrow={2}>
            {active === questions[questions.length - 1].id ? (
              <BrandButton
                size="large"
                variant="contained"
                loading={submitting}
                disabled={!isAllAnswered}
                customColor={customColor}
                onClick={handleSubmit}
              >
                {submitText}
              </BrandButton>
            ) : (
              <BrandButton
                size="large"
                variant="contained"
                disabled={!answers[active]}
                customColor={customColor}
                onClick={handleNext}
              >
                {nextText}
              </BrandButton>
            )}
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
}

function Stepper({
  steps,
  active,
  setActive,
  customColor,
}: {
  steps: string[];
  active: string;
  setActive: Dispatch<SetStateAction<string>>;
  customColor: string;
}) {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={0.5}>
      {steps.map((s) => {
        const width = 100 / (steps.length + 1);
        const isActive = s === active;
        return (
          <Box
            key={s}
            sx={{
              borderTop: `8px solid transparent`,
              borderBottom: `8px solid transparent`,
              transition: 'all 0.2s ease-in-out',

              width: `${isActive ? width * 1.5 : width}%`,

              cursor: 'pointer',
            }}
            onClick={() => setActive(s)}
          >
            <Box
              sx={{
                borderRadius: 4,
                height: 4,
                backgroundColor: isActive
                  ? customColor
                  : theme.palette.grey[300],
              }}
            />
          </Box>
        );
      })}
    </Stack>
  );
}

function LongText({
  placeholder,
  customColor,
  handleAnswer,
}: {
  placeholder: string;
  customColor?: string;
  handleAnswer: (value: string) => void;
}) {
  return (
    <Stack spacing={1.5}>
      <BrandTextField
        // id="outlined-multiline-static"
        placeholder={placeholder}
        multiline
        rows={4}
        variant="outlined"
        customColor={customColor}
        onChange={(e) => handleAnswer(e.target.value)}
      />
    </Stack>
  );
}
function NumberText({
  placeholder,
  customColor,
  handleAnswer,
}: {
  placeholder: string;
  customColor?: string;
  handleAnswer: (value: string) => void;
}) {
  return (
    <Stack spacing={1.5}>
      <BrandTextField
        // id="outlined-multiline-static"
        placeholder={placeholder}
        type="number"
        variant="outlined"
        onChange={(e) => handleAnswer(e.target.value)}
        customColor={customColor}
      />
    </Stack>
  );
}

function MultipleChoice({
  value,
  options,
  customColor,
  handleSelectOption,
}: {
  value?: string;
  options: QuestionOption[];
  customColor?: string;
  handleSelectOption: (answer: string) => void;
}) {
  const checkSize = 24;
  return (
    <Stack spacing={1.5}>
      {options.map((o) => (
        <>
          <RadioStyled
            key={o.id}
            customColor={customColor}
            variant="text"
            size="large"
            onClick={() => handleSelectOption(o.content)}
          >
            {o.content}
            {o.content === value && (
              <Box
                sx={{
                  position: 'absolute',
                  right: -(checkSize / 2),
                  zIndex: 1,
                }}
              >
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    position: 'relative',
                    height: checkSize,
                    width: checkSize,
                    borderRadius: '50%',
                    backgroundColor: '#0DA800',
                  }}
                >
                  <Iconify
                    icon={'zondicons:checkmark'}
                    width={checkSize / 2}
                    height={checkSize / 2}
                    style={{
                      color: '#fff',
                      position: 'relative',
                      zIndex: 1,
                    }}
                    // backgroundColor: `${alpha(theme.palette.primary.main, 0.08)}`,
                  />
                </Stack>
              </Box>
            )}
          </RadioStyled>
        </>
      ))}
    </Stack>
  );
}
function ContactForm({
  defaultCountryCode,
  options,
  customColor,
  handleAnswer,
}: {
  defaultCountryCode: string; // +45
  options: ContactOption[];
  customColor: string;
  handleAnswer: (optionId: string, value: string, type?: 'countryCode') => void;
}) {
  return (
    <Stack spacing={1.5}>
      {options.map((o) => {
        const isEmailField = o.label?.toLowerCase() === 'email';
        if (o.id === phoneFieldId) {
          return (
            <PhoneField
              customColor={customColor}
              name="phone"
              label={o.label}
              placeholder="11 22 33 44"
              onChange={(e) => handleAnswer(o.id, e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment sx={{ width: 200 }} position="start">
                    <PhoneCountryField
                      id="outlined-select-currency"
                      name={'countryCode'}
                      defaultValue={defaultCountryCode}
                      onChange={(e) =>
                        handleAnswer(o.id, e.target.value, 'countryCode')
                      }
                      MenuProps={{
                        MenuListProps: {
                          style: {
                            maxHeight: '200px', // specify your desired maxHeight value here
                          },
                        },
                      }}
                    >
                      {countries.map((option) => (
                        <MenuItem value={option.phone}>
                          {option.flag} {option.phone}
                        </MenuItem>
                      ))}
                    </PhoneCountryField>
                  </InputAdornment>
                ),
              }}
            />
          );
        }
        return (
          <>
            <BrandTextField
              label={o.label}
              placeholder={o.content}
              key={o.id}
              customColor={customColor}
              onChange={(e) => handleAnswer(o.id, e.target.value)}
              type={isEmailField ? 'email' : 'text'}
              required
            />
          </>
        );
      })}
    </Stack>
  );
}

const RadioStyled = styled(Button)<
  ButtonProps & { customColor: any; flatButton?: boolean }
>((props: any) => {
  const { theme } = props;
  const transparent = alpha('#161618', 0.08);
  let hoverColor = 'inherit';
  if (props.customColor) {
    hoverColor = lighten(props.customColor, 0.9);
  }
  return {
    justifyContent: 'flex-start',
    paddingLeft: 16,
    paddingTop: 12,
    paddingBottom: 12,
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'left',
    position: 'relative',
    color: '#161618', //theme.palette.getContrastText(props.customColor), //'#222',
    textTransform: 'none',
    borderRadius: 8,
    backgroundColor: '#fff',
    fontFamily: 'inherit',
    // border: `1px solid ${theme.palette.grey[100]}`,
    boxShadow: `0 0px 16px 2px ${transparent}`,
    zIndex: 1,
    '&:hover': {
      backgroundColor: hoverColor,
      boxShadow: `0 0px 16px 2px ${transparent}`,
    },
  };
});

const BrandButton = styled(LoadingButton)<
  ButtonProps & { customColor: any; flatButton?: boolean }
>((props: any) => {
  const { theme, customColor, flatButton } = props;
  if (!customColor) {
    return {};
  }
  const [r, g, b] = hex2rgb(customColor);
  if (props.variant === 'contained') {
    return {
      borderRadius: 8,
      textTransform: 'none',
      color: theme.palette.getContrastText(customColor),
      backgroundColor: customColor,
      fontFamily: 'inherit',
      boxShadow: flatButton ? 'none' : `0 4px 8px 0 rgb(${r} ${g} ${b} / 24%)`,
      '&:hover': {
        boxShadow: flatButton
          ? 'none'
          : `0 2px 4px 0 rgb(${r} ${g} ${b} / 24%)`,
        backgroundColor: customColor,
      },
    };
  }
  if (props.variant === 'outlined') {
    return {
      borderRadius: 8,
      textTransform: 'none',
      color: customColor,
      borderColor: customColor,
      fontFamily: 'inherit',
      '&:hover': {
        borderColor: customColor,
        backgroundColor: flatButton ? 'none' : `rgba(${r}, ${g}, ${b}, 0.08)`,
      },
    };
  }
  return {
    borderRadius: 8,
    textTransform: 'none',
    color: customColor,
    fontFamily: 'inherit',
    // borderColor: customColor,
    '&:hover': {
      borderColor: customColor,
      backgroundColor: flatButton ? 'none' : `rgba(${r}, ${g}, ${b}, 0.08)`,
    },
  };
});

const BrandTextField = styled(TextField)<
  TextFieldProps & { customColor?: any }
>((props: any) => {
  const { theme, customColor } = props;
  // console.log({ customColor });
  if (!customColor) {
    return {};
  }
  const borderColor = alpha(customColor, 0.2);
  return {
    backgroundColor: '#fff',
    borderRadius: 8,
    fontFamily: 'inherit',
    '& label.Mui-focused': {
      // color: customColor,
      color: '#161618',
    },
    '& .MuiInput-underline:after': {
      // borderBottomColor: 'green',
    },
    '& .MuiOutlinedInput-root': {
      // '& fieldset': {
      //   borderColor: 'red',
      // },
      fieldset: {
        transition: 'all 0.2s ease-in-out',
        borderColor: theme.palette.grey[300],
        fontFamily: 'inherit',
      },
      borderRadius: 8,
      '&:hover fieldset': {
        borderColor: theme.palette.grey[400],
      },
      '&.Mui-focused fieldset': {
        borderColor: borderColor, //theme.palette.grey[300], // borderColor,
      },
    },
  };
});

export const PhoneField: any = styled(TextField)<
  TextFieldProps & { customColor?: any }
>((props: any) => {
  const { theme, customColor } = props;
  // console.log({ customColor });
  if (!customColor) {
    return {};
  }
  const borderColor = alpha(customColor, 0.2);
  return {
    backgroundColor: '#fff',
    borderRadius: 8,
    fontFamily: 'inherit',
    '& label.Mui-focused': {
      // color: customColor,
      color: '#161618',
    },
    '& .MuiInput-underline:after': {
      // borderBottomColor: 'green',
    },
    '& .MuiOutlinedInput-root': {
      // '& fieldset': {
      //   borderColor: 'red',
      // },
      paddingLeft: 0,
      fieldset: {
        fontFamily: 'inherit',
        transition: 'all 0.2s ease-in-out',
        borderColor: theme.palette.grey[300],
      },
      borderRadius: 8,
      '&:hover fieldset': {
        borderColor: theme.palette.grey[400],
      },
      '&.Mui-focused fieldset': {
        borderColor: borderColor, //theme.palette.grey[300], // borderColor,
      },
    },
  };
});
export const PhoneCountryField: any = styled(Select)<TextFieldProps>(
  (props: any) => ({
    '& .MuiOutlinedInput-root': {
      paddingLeft: 0,
      '&.Mui-focused fieldset': {
        border: `2px solid ${props.theme.palette.primary.main} !important`,
      },
    },
    '& fieldset': {
      border: 'none',
    },
    '& select': {
      paddingRight: '0px !important',
    },
  })
);

export const supportedLeadFormLanguages = [Intl.DA, Intl.EN, Intl.NO, Intl.DE];

// NOT ALL LANGUAGES SUPPORTED
export const countries = [
  {
    languageCode: Intl.DA,
    code: 'DK',
    label: 'Danmark',
    language: 'Dansk',
    phone: '+45',
    flag: '🇩🇰',
  },
  {
    languageCode: Intl.FI,
    code: 'FI',
    label: 'Finland',
    language: 'Finnish',
    phone: '+358',
    flag: '🇫🇮',
  },
  {
    languageCode: Intl.SE,
    code: 'SE',
    label: 'Sverige',
    language: 'Svenska',
    phone: '+46',
    flag: '🇸🇪',
  },
  {
    languageCode: Intl.DE,
    code: 'DE',
    label: 'Germany',
    language: 'Deutsch',
    phone: '+49',
    flag: '🇩🇪',
  },
  {
    languageCode: Intl.NO,
    code: 'NO',
    label: 'Norge',
    language: 'Norsk',
    phone: '+47',
    flag: '🇳🇴',
  },
  {
    languageCode: Intl.IT,
    code: 'IT',
    label: 'Italy',
    language: 'Italian',
    phone: '+39',
    flag: '🇮🇹',
  },
  {
    languageCode: Intl.ES,
    code: 'ES',
    label: 'Spain',
    language: 'Spanish',
    phone: '+34',
    flag: '🇪🇸',
  },
  {
    languageCode: Intl.FR,
    code: 'FR',
    label: 'France',
    language: 'French',
    phone: '+33',
    flag: '🇫🇷',
  },
  {
    languageCode: Intl.EN,
    code: 'EN',
    label: 'United Kingdom',
    language: 'English',
    phone: '+44',
    flag: '🇬🇧',
  },
  {
    languageCode: Intl.CH,
    code: 'CH',
    label: 'Switzerland',
    language: 'French',
    phone: '+41',
    flag: '🇨🇭',
  },
  {
    languageCode: Intl.US,
    code: 'US',
    label: 'United States',
    language: 'English',
    phone: '+1',
    flag: '🇺🇸',
  },
  {
    languageCode: Intl.CY,
    code: 'CY',
    label: 'Cyprus',
    language: 'Greek',
    phone: '+357',
    flag: '🇨🇾',
  },
  {
    languageCode: Intl.PT,
    code: 'PT',
    label: 'Portugal',
    language: 'Portugese',
    phone: '+351',
    flag: '🇵🇹',
  },
  {
    languageCode: Intl.ID,
    code: 'ID',
    label: 'Indonesia',
    language: 'Indonesian',
    phone: '+62',
    flag: '🇮🇩',
  },
  {
    languageCode: Intl.AU,
    code: 'AU',
    label: 'Australia',
    language: 'English',
    phone: '+61',
    flag: '🇦🇺',
  },
  {
    languageCode: Intl.BR,
    code: 'BR',
    label: 'Brazil',
    language: 'Portuguese',
    phone: '+55',
    flag: '🇧🇷',
  },
];
